/**
 * "What Amoreg can reach" — one editor for every firm-scoped integration,
 * in the same shape each time:
 *
 *   1. the vendor's own boundary, in its own terms (an admin sets that in
 *      Entra, Google, Laserfiche or RuleGuard — not here);
 *   2. the organization's narrower boundary inside it: named places for a
 *      document store, or named tools picked from the vendor's live
 *      catalogue for RuleGuard;
 *   3. what Amoreg may do there.
 *
 * Saving is explicit — a half-typed folder must not narrow (or widen) what
 * the organization's agents reach until the admin says so.
 *
 * @module components/apps/integrations/IntegrationScopeForm
 */
import { Loader2Icon, PlusIcon, RefreshCwIcon, XIcon } from "lucide-react";
import { useId, useState } from "react";

import { cn } from "~/lib/utils";
import { useIntegrationTools, type IntegrationScope } from "../../../state/governance";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Input } from "../../ui/input";
import {
  DEFAULT_DOCUMENT_STORE_SCOPE,
  DOCUMENT_STORE_OPERATION_LABELS,
  type ScopeSpec,
} from "./integrationContent";

export function IntegrationScopeForm({
  organizationId,
  connectorId,
  configured,
  spec,
  scope,
  disabled,
  canManage,
  onSave,
}: {
  readonly organizationId: string | null;
  readonly connectorId: string;
  /** Whether a credential is stored — a live tool catalogue needs one. */
  readonly configured: boolean;
  readonly spec: ScopeSpec;
  readonly scope: IntegrationScope | null;
  readonly disabled: boolean;
  readonly canManage: boolean;
  readonly onSave: (scope: IntegrationScope) => void;
}) {
  const scopeId = useId();
  const initial = scope ?? DEFAULT_DOCUMENT_STORE_SCOPE;
  const [locations, setLocations] = useState<ReadonlyArray<string>>(initial.locations);
  const [operations, setOperations] = useState<ReadonlyArray<string>>(initial.operations);
  const dirty =
    locations.join("\n") !== initial.locations.join("\n") ||
    [...operations].sort().join(",") !== [...initial.operations].sort().join(",");

  return (
    <div className="space-y-6" data-testid="integration-scope-form">
      <p className="text-sm text-muted-foreground" data-testid="scope-provider-control">
        {spec.providerControl} Below, the organization can narrow that further; Amoreg refuses
        anything outside both.
      </p>

      {spec.kind === "tools" ? (
        <ToolPicker
          organizationId={organizationId}
          connectorId={connectorId}
          configured={configured}
          spec={spec}
          selected={locations}
          disabled={disabled}
          canManage={canManage}
          onChange={setLocations}
        />
      ) : (
        <LocationList
          spec={spec}
          locations={locations}
          disabled={disabled}
          canManage={canManage}
          onChange={setLocations}
        />
      )}

      <div className="space-y-2">
        <span className="text-sm font-medium text-foreground">What Amoreg may do</span>
        <ul className="grid gap-2 sm:grid-cols-2">
          {spec.operations.map((operation) => {
            const meta =
              spec.operationLabels?.[operation] ?? DOCUMENT_STORE_OPERATION_LABELS[operation];
            const checked = operations.includes(operation);
            return (
              <li key={operation}>
                <label
                  htmlFor={`${scopeId}-${operation}`}
                  className="flex cursor-pointer items-start gap-3 rounded-lg border border-border/60 bg-background px-3 py-2.5"
                >
                  <Checkbox
                    id={`${scopeId}-${operation}`}
                    checked={checked}
                    disabled={disabled || !canManage}
                    onCheckedChange={(next) =>
                      setOperations(
                        next
                          ? [...operations, operation]
                          : operations.filter((entry) => entry !== operation),
                      )
                    }
                    aria-label={meta.label}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-foreground">{meta.label}</span>
                    <span className="block text-xs text-muted-foreground">{meta.description}</span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      </div>

      {canManage ? (
        <div className="flex items-center justify-end gap-3">
          {operations.length === 0 ? (
            <span className="text-xs text-muted-foreground">
              With nothing switched on, Amoreg can only list what it could reach.
            </span>
          ) : null}
          <Button
            type="button"
            className="cursor-pointer rounded-full"
            disabled={disabled || !dirty}
            onClick={() =>
              onSave({
                locations,
                operations: spec.operations.filter((entry) => operations.includes(entry)),
              })
            }
          >
            {disabled ? <Loader2Icon className="size-4 animate-spin" /> : null}
            Save scope
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** Named places, typed in: sites, folders, paths. */
function LocationList({
  spec,
  locations,
  disabled,
  canManage,
  onChange,
}: {
  readonly spec: ScopeSpec;
  readonly locations: ReadonlyArray<string>;
  readonly disabled: boolean;
  readonly canManage: boolean;
  readonly onChange: (locations: ReadonlyArray<string>) => void;
}) {
  const [draft, setDraft] = useState("");
  const addLocation = () => {
    const value = draft.trim();
    if (value.length === 0 || locations.includes(value)) return;
    onChange([...locations, value]);
    setDraft("");
  };
  return (
    <div className="space-y-2">
      <span className="text-sm font-medium text-foreground">{spec.locationLabel}</span>
      {locations.length > 0 ? (
        <ul className="space-y-1.5">
          {locations.map((location) => (
            <li
              key={location}
              className="flex items-center gap-2 rounded-lg border border-border/60 bg-background px-3 py-1.5 text-sm"
            >
              <span className="min-w-0 flex-1 truncate font-mono text-xs text-foreground">
                {location}
              </span>
              {canManage ? (
                <button
                  type="button"
                  aria-label={`Remove ${location}`}
                  className="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  disabled={disabled}
                  onClick={() => onChange(locations.filter((entry) => entry !== location))}
                >
                  <XIcon className="size-3.5" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">{spec.unlimitedHint}</p>
      )}
      {canManage ? (
        <div className="flex gap-2">
          <Input
            aria-label={spec.locationLabel}
            className="font-mono text-xs"
            disabled={disabled}
            placeholder={spec.locationPlaceholder}
            value={draft}
            onChange={(event) => setDraft(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                addLocation();
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            className="shrink-0 cursor-pointer rounded-full"
            disabled={disabled || draft.trim().length === 0}
            onClick={addLocation}
          >
            <PlusIcon className="size-4" />
            Add
          </Button>
        </div>
      ) : null}
      <p className="text-xs text-muted-foreground">{spec.locationHint}</p>
    </div>
  );
}

/**
 * Tools picked from the vendor's live catalogue. Each row shows RuleGuard's
 * own description and whether RuleGuard marks the tool read-only, so an
 * admin sees exactly what "look things up" and "change records" would let
 * through before ticking anything.
 */
function ToolPicker({
  organizationId,
  connectorId,
  configured,
  spec,
  selected,
  disabled,
  canManage,
  onChange,
}: {
  readonly organizationId: string | null;
  readonly connectorId: string;
  readonly configured: boolean;
  readonly spec: ScopeSpec;
  readonly selected: ReadonlyArray<string>;
  readonly disabled: boolean;
  readonly canManage: boolean;
  readonly onChange: (selected: ReadonlyArray<string>) => void;
}) {
  const catalogue = useIntegrationTools(configured ? organizationId : null, connectorId);
  const listId = useId();
  const isSelected = (name: string) =>
    selected.some((entry) => entry.toLowerCase() === name.toLowerCase());
  const toggle = (name: string, next: boolean) =>
    onChange(
      next
        ? [...selected, name]
        : selected.filter((entry) => entry.toLowerCase() !== name.toLowerCase()),
    );
  // A name that was allowed earlier but is no longer in the catalogue is
  // still shown, so it can be removed knowingly rather than lost silently.
  // Only once the catalogue has loaded: while it is null nothing is known
  // to be stale, and removing everything would widen the scope to all.
  const availableTools = catalogue.tools;
  const stale =
    availableTools === null
      ? []
      : selected.filter(
          (entry) =>
            !availableTools.some((tool) => tool.name.toLowerCase() === entry.toLowerCase()),
        );

  return (
    <div className="space-y-2" data-testid="tool-picker">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-foreground">{spec.locationLabel}</span>
        {configured && catalogue.tools !== null ? (
          <button
            type="button"
            className="inline-flex cursor-pointer items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            onClick={catalogue.refresh}
            disabled={catalogue.isPending}
          >
            <RefreshCwIcon className={cn("size-3", catalogue.isPending && "animate-spin")} />
            Refresh
          </button>
        ) : null}
      </div>
      {!configured ? (
        <p className="text-sm text-muted-foreground">
          The catalogue appears once the service account is connected above.
        </p>
      ) : catalogue.error !== null ? (
        <p className="text-sm text-destructive" role="alert">
          {catalogue.error}
        </p>
      ) : catalogue.tools === null ? (
        <p className="text-sm text-muted-foreground">Reading the catalogue from RuleGuard…</p>
      ) : catalogue.tools.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          RuleGuard offers this account no tools. Check its roles in RuleGuard.
        </p>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            {selected.length === 0
              ? spec.unlimitedHint
              : `${selected.length} of ${catalogue.tools.length} tools allowed.`}
          </p>
          <ul className="max-h-80 space-y-1.5 overflow-y-auto pr-1">
            {catalogue.tools.map((tool) => (
              <li key={tool.name}>
                <label
                  htmlFor={`${listId}-${tool.name}`}
                  className="flex cursor-pointer items-start gap-3 rounded-lg border border-border/60 bg-background px-3 py-2"
                >
                  <Checkbox
                    id={`${listId}-${tool.name}`}
                    checked={isSelected(tool.name)}
                    disabled={disabled || !canManage}
                    onCheckedChange={(next) => toggle(tool.name, next === true)}
                    aria-label={tool.name}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs font-medium text-foreground">
                        {tool.name}
                      </span>
                      <span
                        className={cn(
                          "rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                          tool.effect === "read"
                            ? "bg-success/10 text-success"
                            : "bg-muted text-muted-foreground",
                        )}
                        title={
                          tool.effect === "read"
                            ? "RuleGuard marks this tool read-only"
                            : tool.effectSource === "annotation"
                              ? "RuleGuard marks this tool as changing records"
                              : "RuleGuard does not say; treated as changing records"
                        }
                      >
                        {tool.effect === "read" ? "read-only" : "may change records"}
                      </span>
                    </span>
                    {tool.description ? (
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        {tool.description}
                      </span>
                    ) : null}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </>
      )}
      {stale.length > 0 ? (
        <ul className="space-y-1.5">
          {stale.map((name) => (
            <li
              key={name}
              className="flex items-center gap-2 rounded-lg border border-dashed border-border/60 px-3 py-1.5 text-sm"
            >
              <span className="min-w-0 flex-1 truncate font-mono text-xs text-muted-foreground">
                {name}
              </span>
              <span className="text-xs text-muted-foreground">not in the catalogue</span>
              {canManage ? (
                <button
                  type="button"
                  aria-label={`Remove ${name}`}
                  className="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  disabled={disabled}
                  onClick={() => toggle(name, false)}
                >
                  <XIcon className="size-3.5" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      <p className="text-xs text-muted-foreground">{spec.locationHint}</p>
    </div>
  );
}
